import { createFileRoute } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useMemo } from 'react';
import {
  useSelfServiceListTickets,
  useSelfServiceListBearers,
} from '@/api/trafikportalen';
import { toast } from '@/hooks/useToast';
import type {
  SelfServiceTicketEntryDTO,
  SelfServiceBearerEntryDTO,
} from '@/api/trafikportalen';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { CenteredColumn } from '@/components/CenteredColumn';
import { SelfServiceTravellerSection } from '@/components/SelfService/SelfServiceTravellerSection';
import { useTenantID } from '@/hooks/useTenantID';

export const Route = createFileRoute(
  '/$tenantId/self-service/_layout/dashboard',
)({
  component: Dashboard,
});

export interface MergedTravellerData {
  travellerId: string;
  name: string;
  organizationName?: string;
  travellerEmail?: string;
  ticketEntry?: SelfServiceTicketEntryDTO;
  bearerEntry?: SelfServiceBearerEntryDTO;
}

function mergeTravellerData(
  ticketEntries: SelfServiceTicketEntryDTO[],
  bearerEntries: SelfServiceBearerEntryDTO[],
): MergedTravellerData[] {
  const map = new Map<string, MergedTravellerData>();

  for (const entry of ticketEntries) {
    map.set(entry.traveller_id, {
      travellerId: entry.traveller_id,
      name: `${entry.first_name} ${entry.last_name}`,
      ticketEntry: entry,
    });
  }

  for (const entry of bearerEntries) {
    const existing = map.get(entry.traveller_id);
    if (existing) {
      existing.bearerEntry = entry;
      existing.organizationName ??= entry.organization_name;
      existing.travellerEmail ??= entry.bobcat_private_email;
    } else {
      map.set(entry.traveller_id, {
        travellerId: entry.traveller_id,
        name: entry.name,
        organizationName: entry.organization_name,
        travellerEmail: entry.bobcat_private_email,
        bearerEntry: entry,
      });
    }
  }

  return [...map.values()];
}

function Dashboard() {
  const tenantId = useTenantID();

  const { data: ticketsData, isLoading: ticketsLoading } =
    useSelfServiceListTickets();
  const {
    data: bearersData,
    isLoading: bearersLoading,
    isError: bearersError,
  } = useSelfServiceListBearers();

  const { formatMessage } = useIntl();

  useEffect(() => {
    if (bearersError) {
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Kunde inte hämta biljettbärare',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [bearersError, formatMessage]);

  const isLoading = ticketsLoading || bearersLoading;

  const selfTravellers = useMemo(
    () => mergeTravellerData(ticketsData?.self ?? [], bearersData?.self ?? []),
    [ticketsData?.self, bearersData?.self],
  );

  const wardTravellers = useMemo(
    () =>
      mergeTravellerData(ticketsData?.wards ?? [], bearersData?.wards ?? []),
    [ticketsData?.wards, bearersData?.wards],
  );

  if (isLoading) {
    return (
      <div className="flex grow items-center justify-center">
        <Loading />
      </div>
    );
  }

  const hasData = selfTravellers.length > 0 || wardTravellers.length > 0;

  return (
    <CenteredColumn className={tenantId}>
      <h1 className="text-2xl font-medium text-text-950">
        <FormattedMessage defaultMessage="Självbetjäning" />
      </h1>

      {!hasData ? (
        <Section className="items-center justify-center py-12">
          <p className="text-text-700">
            <FormattedMessage defaultMessage="Du har inga biljetter eller kort registrerade." />
          </p>
        </Section>
      ) : (
        <div className="flex flex-col gap-6">
          {selfTravellers.map((traveller) => (
            <SelfServiceTravellerSection
              key={traveller.travellerId}
              data={traveller}
            />
          ))}

          {wardTravellers.map((traveller) => (
            <SelfServiceTravellerSection
              key={traveller.travellerId}
              data={traveller}
              wardOf
            />
          ))}
        </div>
      )}
    </CenteredColumn>
  );
}
